import { createContext, useContext, useState } from "react";
import { CountByAirStatusModel } from "entities/AirSavedCard/model/types";
import { CountBySeaStatusModel } from "entities/SeaSavedCard/model/types";
import { AirStatusesModel } from "entities/shared/airCard.types";
import { SeaStatusesModel } from "entities/shared/seaCard.types";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { FilterContextType } from "features/shared/filter.types";
import { searchFilterStore } from "entities/SearchFilter";
import { useAppSelector } from "shared/lib/redux";
import { CompatibleString, ValueOf } from "shared/lib/typeCheck.lib";

import { useAirFilterContext } from "../../model/useAirFilterContex";
import { useSeaFilterContext } from "../../model/useSeaFilterContext";

const FilterContext = createContext(null as unknown as FilterContextType);
FilterContext.displayName = "FilterContext";

export const FilterProvider: RFC = ({ children }) => {
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [statusFilter, setStatusFilter] =
    useState<CompatibleString<ValueOf<typeof SeaStatusesModel> | ValueOf<typeof AirStatusesModel>>>("");
  const [allStatuses, setAllStatuses] = useState<CountBySeaStatusModel[] | CountByAirStatusModel[]>([]);
  const {
    seaFilter,
    seaFilterInnerValues,
    setSeaInnerWhereQuery,
    setSeaInnerOrderQuery,
    resetSeaInnerFilter,
    saveSeaFilter,
    resetSeaFilter,
    seaFilterChanged,
    isSeaFilter,
  } = useSeaFilterContext();
  const {
    airFilter,
    airFilterInnerValues,
    setAirInnerWhereQuery,
    setAirInnerOrderQuery,
    resetAirInnerFilter,
    saveAirFilter,
    resetAirFilter,
    airFilterChanged,
    isAirFilter,
  } = useAirFilterContext();

  const filterContextValue = (): FilterContextType => {
    const defaultContextValues = {
      statusFilter,
      setStatusFilter,
      allStatuses,
      setAllStatuses,
    };
    switch (shipment) {
      case ShipmentType.AIR:
        return {
          ...defaultContextValues,
          filter: airFilter,
          setFilter: saveAirFilter,
          resetFilter: resetAirFilter,
          filterChanged: airFilterChanged,
          innerFilterState: {
            filter: airFilterInnerValues,
            setWhereQuery: setAirInnerWhereQuery,
            setOrderQuery: setAirInnerOrderQuery,
            resetFilter: resetAirInnerFilter,
          },
          isOpenFilter,
          setOpenFilter,
          isAirFilter,
        };
      case ShipmentType.SEA:
      default:
        return {
          ...defaultContextValues,
          filter: seaFilter,
          setFilter: saveSeaFilter,
          resetFilter: resetSeaFilter,
          filterChanged: seaFilterChanged,
          innerFilterState: {
            filter: seaFilterInnerValues,
            setWhereQuery: setSeaInnerWhereQuery,
            setOrderQuery: setSeaInnerOrderQuery,
            resetFilter: resetSeaInnerFilter,
          },
          isOpenFilter,
          setOpenFilter,
          isSeaFilter,
        };
    }
  };

  return <FilterContext.Provider value={filterContextValue()}>{children}</FilterContext.Provider>;
};
FilterProvider.displayName = FilterProvider.name;

export const useFilterContext = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("No context found for Filter");
  }
  return context;
};
